/**
 * RTWO Event Bus — Topic Registry Service
 */

import { EVENT_BUS_SERVICE_IDS, RTWO_TOPIC_REGISTRY } from '../domain-models';
import type { RTWOEventTopic, RTWOTopicStatus, TopicId } from '../domain-models';
import type { ITopicRegistryService, TopicRegistryResult } from '../service-interfaces';
import type { EBusRepositories } from '../repositories/ebus-repositories';
import { BaseEBusService, type EBusServiceContext } from './base-ebus-service';

export class TopicRegistryService extends BaseEBusService implements ITopicRegistryService {
  readonly serviceId = EVENT_BUS_SERVICE_IDS.TOPIC_REGISTRY;
  readonly serviceName = 'Topic Registry';
  readonly description =
    'Registry of architecture-defined RTWO event topics. No active publishers.';
  readonly integrations = ['All RTWO Engines', 'Event Registry', 'Routing Engine'];
  readonly requiresRealEventSources = false;

  private readonly architectureTopics: RTWOEventTopic[];

  constructor(
    context: EBusServiceContext,
    private readonly repositories: EBusRepositories
  ) {
    super(context);
    this.architectureTopics = RTWO_TOPIC_REGISTRY.map((topic) => ({
      topicId: topic.topicId,
      topicName: topic.topicName,
      description: topic.description,
      status: topic.status,
    }));
  }

  async initialize(): Promise<void> {
    this.status = 'structure_ready';
    for (const topic of this.architectureTopics) {
      this.repositories.topics.save(topic);
    }
  }

  async registerTopic(_topicName: string, _description: string): Promise<TopicRegistryResult> {
    return {
      success: false,
      topicId: null,
      error:
        'Topic Registry Not Active — Topics are pre-defined in architecture. No dynamic registration until real event sources are connected.',
      isPlaceholder: true,
      source: 'Event Bus — Topic Registry Not Active',
    };
  }

  getAllTopics(): RTWOEventTopic[] {
    return this.architectureTopics;
  }

  getTopic(topicId: TopicId): RTWOEventTopic | null {
    return this.architectureTopics.find((topic) => topic.topicId === topicId) ?? null;
  }

  getTopicsByStatus(status: RTWOTopicStatus): RTWOEventTopic[] {
    return this.architectureTopics.filter((topic) => topic.status === status);
  }

  getTopicCount(): number {
    return this.architectureTopics.length;
  }
}
